/**
 * 简化版 VCF/VCT 算杀算法
 * 避免复杂的递归和错误处理
 */
export class SimpleVCFVCTSolver {
    constructor(patternRecognizer) {
        this.patternRecognizer = patternRecognizer;
        this.maxVCFDepth = 6; // VCF最大搜索深度
        this.maxVCTDepth = 4; // VCT最大搜索深度
    }

    /**
     * 简化VCF搜索
     * @param {Board} board 棋盘
     * @param {number} player 玩家
     * @returns {Object} VCF结果
     */
    simpleVCFSearch(board, player) {
        // 获取所有冲四点
        const fourMoves = this.getFourMoves(board, player);

        if (fourMoves.length === 0) {
            return { success: false, moves: [] };
        }

        // 测试每个冲四点
        for (const move of fourMoves) {
            const testBoard = board.clone();
            testBoard.makeMove(move.row, move.col);

            if (testBoard.checkWin(move.row, move.col)) {
                return { success: true, moves: [move], type: 'VCF' };
            }

            // 检查对手能否防守
            const opponent = 3 - player;
            const canDefend = this.canOpponentDefend(testBoard, opponent);

            if (!canDefend) {
                return { success: true, moves: [move], type: 'VCF' };
            }
        }

        return { success: false, moves: [] };
    }

    /**
     * 简化VCT搜索
     * @param {Board} board 棋盘
     * @param {number} player 玩家
     * @returns {Object} VCT结果
     */
    simpleVCTSearch(board, player) {
        // 获取所有威胁点
        const threatMoves = this.getThreatMoves(board, player);

        if (threatMoves.length === 0) {
            return { success: false, moves: [] };
        }

        // 按威胁强度排序
        threatMoves.sort((a, b) => {
            const scoreA = this.getThreatScore(a.type);
            const scoreB = this.getThreatScore(b.type);
            return scoreB - scoreA;
        });

        // 测试前3个最强威胁
        for (let i = 0; i < Math.min(3, threatMoves.length); i++) {
            const move = threatMoves[i];
            const testBoard = board.clone();
            testBoard.makeMove(move.row, move.col);

            if (testBoard.checkWin(move.row, move.col)) {
                return { success: true, moves: [move], type: 'VCT' };
            }

            // 简化防守检查
            const opponent = 3 - player;
            const defenses = this.getSimpleDefenses(testBoard, opponent);

            let allDefensesFail = true;
            for (const defense of defenses) {
                const defenseBoard = testBoard.clone();
                defenseBoard.makeMove(defense.row, defense.col);

                // 检查是否还能形成威胁
                const stillHasThreats = this.hasThreats(defenseBoard, player);
                if (!stillHasThreats) {
                    allDefensesFail = false;
                    break;
                }
            }

            if (allDefensesFail) {
                return { success: true, moves: [move], type: 'VCT' };
            }
        }

        return { success: false, moves: [] };
    }

    /**
     * 获取冲四点
     * @param {Board} board 棋盘
     * @param {number} player 玩家
     * @returns {Array} 冲四点数组
     */
    getFourMoves(board, player) {
        const fourMoves = [];
        const emptyPositions = board.getEmptyPositions();

        for (const pos of emptyPositions) {
            if (this.willFormFour(board, pos, player)) {
                fourMoves.push({
                    ...pos,
                    type: 'four'
                });
            }
        }

        return fourMoves;
    }

    /**
     * 获取威胁走法
     * @param {Board} board 棋盘
     * @param {number} player 玩家
     * @returns {Array} 威胁走法数组
     */
    getThreatMoves(board, player) {
        const threatMoves = [];
        const emptyPositions = board.getEmptyPositions();

        for (const pos of emptyPositions) {
            const threatType = this.getThreatType(board, pos, player);
            if (threatType) {
                threatMoves.push({
                    ...pos,
                    type: threatType
                });
            }
        }

        return threatMoves;
    }

    /**
     * 判断是否形成四
     * @param {Board} board 棋盘
     * @param {Object} pos 位置
     * @param {number} player 玩家
     * @returns {boolean} 是否会形成四
     */
    willFormFour(board, pos, player) {
        for (const direction of this.patternRecognizer.directions) {
            const { count } = this.patternRecognizer.countPattern(board, pos.row, pos.col, player, direction);

            if (count >= 4) {
                return true;
            }
        }

        return false;
    }

    /**
     * 获取威胁类型
     * @param {Board} board 棋盘
     * @param {Object} pos 位置
     * @param {number} player 玩家
     * @returns {string|null} 威胁类型
     */
    getThreatType(board, pos, player) {
        let hasFour = false;
        let hasOpenThree = false;
        let openThreeCount = 0;

        for (const direction of this.patternRecognizer.directions) {
            const { count, block, space } = this.patternRecognizer.countPattern(board, pos.row, pos.col, player, direction);

            if (count >= 4) {
                hasFour = true;
            } else if (count === 3 && block === 0 && space.length >= 2) {
                openThreeCount++;
            }
        }

        if (hasFour) {
            return 'four';
        } else if (openThreeCount >= 2) {
            return 'double_three';
        } else if (openThreeCount >= 1) {
            return 'open_three';
        }

        return null;
    }

    /**
     * 获取威胁分数
     * @param {string} threatType 威胁类型
     * @returns {number} 威胁分数
     */
    getThreatScore(threatType) {
        const scores = {
            'four': 10000,
            'double_three': 8000,
            'open_three': 3000
        };

        return scores[threatType] || 0;
    }

    /**
     * 检查对手能否防守
     * @param {Board} board 棋盘
     * @param {number} opponent 对手
     * @returns {boolean} 能否防守
     */
    canOpponentDefend(board, opponent) {
        const defenses = this.getSimpleDefenses(board, opponent);

        for (const defense of defenses) {
            const testBoard = board.clone();
            testBoard.makeMove(defense.row, defense.col);

            // 检查是否化解了威胁
            if (!this.hasThreats(testBoard, 3 - opponent)) {
                return true; // 对手能防守
            }
        }

        return false; // 对手无法防守所有威胁
    }

    /**
     * 获取简化防守走法
     * @param {Board} board 棋盘
     * @param {number} player 玩家
     * @returns {Array} 防守走法数组
     */
    getSimpleDefenses(board, player) {
        const defenses = [];
        const emptyPositions = board.getEmptyPositions();

        for (const pos of emptyPositions) {
            if (this.isNearPieces(board, pos)) {
                defenses.push(pos);
            }
        }

        return defenses.slice(0, 8); // 限制数量避免搜索过深
    }

    /**
     * 检查是否有威胁
     * @param {Board} board 棋盘
     * @param {number} player 玩家
     * @returns {boolean} 是否有威胁
     */
    hasThreats(board, player) {
        for (const direction of this.patternRecognizer.directions) {
            const positions = this.getLinePieces(board, direction);

            let count = 0;
            let openEnds = 0;

            for (const pos of positions) {
                if (board.board[pos.row][pos.col] === player) {
                    count++;
                } else if (board.board[pos.row][pos.col] === 0) {
                    openEnds++;
                }
            }

            if (count >= 3 && openEnds >= 2) {
                return true; // 有活三或更强威胁
            }
        }

        return false;
    }

    /**
     * 获取线上的棋子位置
     * @param {Board} board 棋盘
     * @param {Array} direction 方向
     * @returns {Array} 位置数组
     */
    getLinePieces(board, direction) {
        const positions = [];
        const [dr, dc] = direction;

        // 获取棋盘上已有棋子周围的位置
        for (let r = 0; r < board.size; r++) {
            for (let c = 0; c < board.size; c++) {
                if (board.board[r][c] !== 0) {
                    // 检查这个位置周围6格内是否有空位
                    let hasEmpty = false;
                    for (let i = -3; i <= 3; i++) {
                        const nr = r + i * dr;
                        const nc = c + i * dc;
                        if (nr >= 0 && nr < board.size && nc >= 0 && nc < board.size) {
                            if (board.board[nr][nc] === 0) {
                                hasEmpty = true;
                                break;
                            }
                        }
                    }

                    if (hasEmpty) {
                        positions.push({ row: r, col: c });
                    }
                }
            }
        }

        return positions;
    }

    /**
     * 检查位置周围是否有棋子
     * @param {Board} board 棋盘
     * @param {Object} pos 位置
     * @returns {boolean} 是否有棋子
     */
    isNearPieces(board, pos) {
        const { row, col } = pos;

        for (let dr = -2; dr <= 2; dr++) {
            for (let dc = -2; dc <= 2; dc++) {
                const r = row + dr;
                const c = col + dc;

                if (r >= 0 && r < board.size && c >= 0 && c < board.size) {
                    if (board.board[r][c] !== 0) {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    /**
     * 简化算杀主入口
     * @param {Board} board 棋盘
     * @param {number} player 玩家
     * @returns {Object} 算杀结果
     */
    solve(board, player) {
        // 首先尝试VCF
        const vcfResult = this.simpleVCFSearch(board, player);
        if (vcfResult.success) {
            return {
                success: true,
                type: 'VCF',
                moves: vcfResult.moves,
                depth: vcfResult.moves.length
            };
        }

        // 然后尝试VCT
        const vctResult = this.simpleVCTSearch(board, player);
        if (vctResult.success) {
            return {
                success: true,
                type: 'VCT',
                moves: vctResult.moves,
                depth: vctResult.moves.length
            };
        }

        return {
            success: false,
            type: 'none',
            moves: [],
            depth: 0
        };
    }
}